import { create } from 'zustand'
import { startScraperJob, getScraperJobStatus, fetchScraperAnnonces, registerImmobilierSearch, cancelScraperJob } from '../services/api.js'

// États du cycle de vie d'une recherche immobilière
// idle → scraping → polling → fetching → done | error
export const useImmobilierSearchStore = create((set, get) => ({
  status: 'idle',   // 'idle' | 'scraping' | 'polling' | 'fetching' | 'done' | 'error'
  error: null,
  jobId: null,
  searchId: null,
  filters: null,
  annonces: [],
  total: 0,
  pollCount: 0,
  refreshing: false,
  _pollTimer: null,

  startSearch: async (filters, _retryCount = 0) => {
    const { _clearTimer } = get()
    _clearTimer()

    if (_retryCount === 0) {
      set({ status: 'scraping', error: null, jobId: null, searchId: null, annonces: [], total: 0, filters, pollCount: 0, refreshing: false })
    }

    if (get().status !== 'scraping') return

    try {
      const nbPages = filters.nbPages || 2
      const result = await startScraperJob(filters.location || null, nbPages)
      if (get().status !== 'scraping') return
      set({ status: 'polling', jobId: result.job_id })
      get()._schedulePoll()
    } catch (err) {
      if (get().status !== 'scraping') return

      const httpStatus = err?.response?.status
      const detail = err?.response?.data?.detail || err?.message || ''
      const is409 = httpStatus === 409 || detail.toLowerCase().includes('déjà en cours') || detail.toLowerCase().includes('already')

      if (is409 && _retryCount < 10) {
        const timer = setTimeout(() => {
          if (get().status === 'scraping') get().startSearch(filters, _retryCount + 1)
        }, 30000)
        set({ _pollTimer: timer })
      } else {
        set({ status: 'error', error: is409 ? 'Serveur de scraping occupé. Veuillez réessayer dans quelques minutes.' : (detail || 'Erreur de démarrage') })
      }
    }
  },

  _schedulePoll: () => {
    const timer = setTimeout(async () => {
      const { jobId, filters, status } = get()
      if (status !== 'polling' || !jobId) return

      try {
        set((s) => ({ pollCount: s.pollCount + 1 }))
        const statusData = await getScraperJobStatus(jobId)
        const jobStatus = (statusData.status || '').toLowerCase()

        if (jobStatus === 'done') {
          // Délai 5s pour la cohérence DB immédiate
          // Note : les téléphones PAP continuent d'arriver en background pendant 15-20 min
          await new Promise(resolve => setTimeout(resolve, 5000))
          if (get().status !== 'polling') return

          set({ status: 'fetching' })
          const data = await fetchScraperAnnonces(filters)
          try {
            const reg = await registerImmobilierSearch(filters, data.total)
            set({ searchId: reg.searchId })
          } catch {
            // Pas bloquant
          }
          set({ status: 'done', annonces: data.annonces || [], total: data.total || 0 })
        } else if (jobStatus === 'error') {
          set({ status: 'error', error: statusData.message || 'Le scraping a échoué côté serveur.' })
        } else {
          get()._schedulePoll()
        }
      } catch (err) {
        if (get().pollCount < 60) {
          get()._schedulePoll()
        } else {
          set({ status: 'error', error: "Délai d'attente dépassé. Veuillez réessayer." })
        }
      }
    }, 15000)

    set({ _pollTimer: timer })
  },

  // Re-fetch depuis /api/annonces sans débiter de crédits — pour récupérer les téléphones PAP
  refreshAnnonces: async () => {
    const { filters, refreshing } = get()
    if (!filters || refreshing) return
    set({ refreshing: true })
    try {
      const data = await fetchScraperAnnonces(filters)
      set({ annonces: data.annonces || [], total: data.total || 0 })
    } catch {
      // Non bloquant
    } finally {
      set({ refreshing: false })
    }
  },

  _clearTimer: () => {
    const { _pollTimer } = get()
    if (_pollTimer) { clearTimeout(_pollTimer); set({ _pollTimer: null }) }
  },

  reset: () => {
    const { _clearTimer, jobId } = get()
    _clearTimer()
    if (jobId) cancelScraperJob(jobId)
    set({ status: 'idle', error: null, jobId: null, searchId: null, annonces: [], total: 0, filters: null, pollCount: 0, refreshing: false })
  },
}))
